/**
 *  Returns true if the provided value is a number or a numeric string.
 * @param val The value to check.
 * @returns `true` if the value is a number or a numeric string, otherwise `false`.
 */
export const isNumber = (val?: string | number | null | void): boolean =>
  typeof val !== "undefined" &&
  val !== null &&
  !(typeof val === "string" && val.trim() === "") &&
  !isNaN(+val);

/**
 *  Converts a value to an integer if possible.
 * @param val The value to convert.
 * @returns The integer value, or `null` when the value is blank or not numeric.
 */
export const getInt = (val: string | number | null | undefined): null | number =>
  isNumber(val) ? parseInt(String(val), 10) : null;

/**
 * Converts a value to a float if possible.
 * @param val The value to convert.
 * @returns The float value, or `null` when the value is blank or not numeric.
 */
export const getFloat = (val: string | number | null | undefined): null | number =>
  isNumber(val) ? parseFloat(String(val)) : null;

/**
 * Returns is a value is an integer.
 *
 * @remarks
 * 	Number.isInteger('123')
 *  > false
 *  this function will return true in this case
 * @returns `true` if value is an integer
 *
 */
//
export const isInt = (val?: string | number | null | void): boolean => {
  if (isNumber(val)) {
    if (typeof val === "string") {
      return val.match(/[^\d]+/g) === null;
    } else {
      return Number.isInteger(val);
    }
  }
  return false;
};

/**
 * Rounds a number to a specified number of decimal places.
 * @param number The number to round.
 * @param decimalPlaces The number of decimal places to round to.
 * @returns The rounded number.
 */
export const roundPlaces = (number: number, decimalPlaces: number) =>
  Number(Math.round(Number(number + "e" + decimalPlaces)) + "e" + decimalPlaces * -1);

/**
 * Returns a random integer between min and max, inclusive.
 * @param min The minimum integer value.
 * @param max The maximum integer value.
 * @returns A random integer between min and max.
 */
export const randomInt = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Returns the ordinal suffix of a number (e.g., "1st", "2nd", "3rd", "4th").
 * @param number The number to get the ordinal suffix for.
 * @returns The number with its ordinal suffix as a string.
 */
export const getNumberOrdinal = (number: number): string => {
  const ordinals = ["th", "st", "nd", "rd"];
  const modValue = number % 100;
  return number + (ordinals[(modValue - 20) % 10] || ordinals[modValue] || ordinals[0]);
};
